import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useProjectIntakeOptions, useCreateProject } from '../../../hooks/useProjectIntake.js';
import { PROJECT_KINDS } from '../../../types/project-intake.js';
import { isHttpUrl } from '../../../lib/url.js';
import {
  StepIndicator,
  BUSINESS_ORDER,
  AIRCLOSET_TEAM_TOP,
  orderByList,
} from './ProjectIntakeControls.js';
import { INITIAL_FORM, type FormState } from './form-state.js';
import { MOCK_OPTIONS } from './dev-mock-options.js';
import { ProjectIntakeStep1Form } from './ProjectIntakeStep1Form.js';
import { ProjectIntakeStep2Form } from './ProjectIntakeStep2Form.js';
import { ProjectIntakeResult } from './ProjectIntakeResult.js';
import { FassychanLoadingOverlay } from '../../ui/FassychanLoadingOverlay/index.js';

/** @graph-connects none */
export interface ProjectIntakeModalProps {
  /** モーダルの表示状態 */
  open: boolean;
  /** モーダルを閉じる関数 */
  onClose: () => void;
}

/**
 * 案件起票モーダル。Step1（依頼情報）→ Step2（プロジェクト憲章、任意）の 2 ステップ。
 * OK で Backlog チケット + Slack チャンネル + 案件仕様書を作成し、完了画面で各リンクを表示する。
 * 各ステップ／完了画面の表示は Step1Form / Step2Form / Result 子コンポーネントへ委譲する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件起票フォーム。依頼情報とプロジェクト憲章を入力し、起票 API を呼んで作成結果リンクを表示する
 * @graph-connects useProjectIntakeOptions [calls] 選択肢取得
 * @graph-connects useCreateProject [calls] 起票実行
 */
export function ProjectIntakeModal({ open, onClose }: ProjectIntakeModalProps): ReactNode {
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [showErrors, setShowErrors] = useState(false);

  const optionsQuery = useProjectIntakeOptions(open);
  const createMutation = useCreateProject();
  const { reset: resetMutation } = createMutation;

  // 開くたびに状態をリセットする（resetMutation は安定参照）。
  useEffect(() => {
    if (open) {
      setStep(1);
      setForm(INITIAL_FORM);
      setShowErrors(false);
      resetMutation();
    }
  }, [open, resetMutation]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // バックエンド未実装時は DEV に限りモック選択肢へフォールバック（Step2 デザイン確認用）。
  const options = optionsQuery.data ?? (import.meta.env.DEV ? MOCK_OPTIONS : undefined);

  const businessOptions = useMemo(
    () =>
      orderByList(options?.businesses ?? [], BUSINESS_ORDER, (b) => b).map((b) => ({
        value: b,
        label: b,
      })),
    [options],
  );
  const teamOptions = useMemo(() => {
    const teams = options?.teamsByBusiness[form.business] ?? [];
    const ordered =
      form.business === 'エアクロ' ? orderByList(teams, AIRCLOSET_TEAM_TOP, (t) => t) : teams;
    return ordered.map((t) => ({ value: t, label: t }));
  }, [options, form.business]);
  const memberOptions = useMemo(
    () => (options?.members ?? []).map((m) => ({ value: m.nickname, label: m.nickname })),
    [options],
  );
  const kindOptions = PROJECT_KINDS.map((k) => ({ value: k, label: k }));

  const step1Missing =
    !form.kind ||
    !form.business ||
    !form.team ||
    !form.plNickname ||
    !form.mainWorkerNickname ||
    !form.projectName.trim();

  // サーバー側 schema の roughImageUrl.url() と対を成す入口検証。
  // 未入力は任意なので許容し、入力がある場合のみ http/https 形式を要求する。
  const roughImageUrlInvalid =
    form.roughImageUrl.trim() !== '' && !isHttpUrl(form.roughImageUrl.trim());

  const err = (missing: boolean): string | undefined =>
    showErrors && missing ? '必須項目です' : undefined;

  const roughUrlError =
    showErrors && roughImageUrlInvalid
      ? 'URL の形式が正しくありません（http:// または https://）'
      : undefined;

  const handleNext = () => {
    if (step1Missing || roughImageUrlInvalid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setStep(2);
  };

  const handleSubmit = () => {
    if (step1Missing || !form.kind || roughImageUrlInvalid) {
      setShowErrors(true);
      setStep(1);
      return;
    }
    createMutation.mutate({
      kind: form.kind,
      business: form.business,
      team: form.team,
      plNickname: form.plNickname,
      mainWorkerNickname: form.mainWorkerNickname,
      projectName: form.projectName.trim(),
      isAbTest: form.isAbTest,
      releaseHopeDate: form.releaseHopeDate || null,
      roughImageUrl: form.roughImageUrl.trim() || undefined,
      charter: {
        purpose: form.purpose || undefined,
        background: form.background || undefined,
        goal: form.goal || undefined,
        deliverables: form.deliverables || undefined,
        preconditions: form.preconditions || undefined,
        constraints: form.constraints || undefined,
        schedule: form.schedule || undefined,
        cost: form.cost || undefined,
        structure: form.structure || undefined,
        notes: form.notes || undefined,
      },
    });
  };

  const result = createMutation.data;

  const title = result ? '起票が完了しました 🎉' : step === 1 ? '依頼情報' : 'プロジェクト憲章';
  const description = result
    ? undefined
    : step === 1
      ? '案件の基本情報を入力してください（Step 1 / 2）'
      : 'プロジェクト憲章（任意）。空欄のままでも起票でき、後から案件仕様書で追記できます（Step 2 / 2）';

  return (
    <>
      {open && createMutation.isPending && (
        <FassychanLoadingOverlay
          label="起票処理中..."
          portal
          zIndexClassName="z-[calc(var(--z-modal)+1)]"
        />
      )}
      <Modal
        open={open}
        onClose={onClose}
        size="lg"
        className="rounded-3xl md:max-w-5xl"
        title={title}
        description={description}
        footer={renderFooter()}
      >
        <div className="space-y-5">
          <StepIndicator current={result ? 3 : step} />
          {import.meta.env.DEV && !optionsQuery.data && (
            <p className="text-center text-xs text-[var(--color-text-muted)]">
              （DEV プレビュー: 選択肢はモックデータです）
            </p>
          )}
          {result ? (
            <ProjectIntakeResult result={result} />
          ) : (
            <div className="space-y-4">
              {optionsQuery.isError && !options && (
                <p className="text-sm text-[var(--color-error)]">
                  選択肢の取得に失敗しました。時間をおいて再度お試しください。
                </p>
              )}
              {step === 1 ? (
                <ProjectIntakeStep1Form
                  form={form}
                  set={set}
                  err={err}
                  roughUrlError={roughUrlError}
                  loading={optionsQuery.isLoading}
                  kindOptions={kindOptions}
                  businessOptions={businessOptions}
                  teamOptions={teamOptions}
                  memberOptions={memberOptions}
                />
              ) : (
                <ProjectIntakeStep2Form form={form} set={set} />
              )}
              {createMutation.isError && (
                <p className="text-sm text-[var(--color-error)]">
                  起票に失敗しました:{' '}
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : '不明なエラー'}
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );

  /** @graph-connects none */
  function renderFooter(): ReactNode {
    if (result) {
      return (
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>
            閉じる
          </Button>
        </div>
      );
    }
    if (step === 1) {
      return (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button variant="primary" onClick={handleNext}>
            次へ →
          </Button>
        </div>
      );
    }
    return (
      <div className="flex justify-between gap-2">
        <Button
          variant="ghost"
          onClick={() => {
            setStep(1);
          }}
        >
          ← 戻る
        </Button>
        <Button variant="primary" loading={createMutation.isPending} onClick={handleSubmit}>
          OK（起票する）
        </Button>
      </div>
    );
  }
}
